import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, useOutletContext } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { Box, Checkbox } from "@mui/material";
import { baseApi, TOPBARHEIGHT } from "../common/constants";
import { Axios, getDisplayableCells } from "../common/utils";
import { AppWrap, ContentArea, GridArea, H1, H2 } from "../common/commonComps";
import { LogoBox } from "./subComps";
import ibs from "./images/ibs.png";
import type { Size, Store } from "../types";

const FilterBox = styled(`div`)({
    border: `1px solid #a3a3a3`,
    padding: `5px 0 5px 5px`,
});

const RightBox = styled(`div`)<{ height?: number | string }>(({ height }) => ({
    height: `${height ? height : `200`}px`,
    width: `100%`,
    boxShadow: `0px 2px 7px 0px #00000040`,
    margin: `15px 0 30px`,
    overflow: `auto`,
}));

const Search = ({
    size,
    storeCellsAcross,
    contentAreaWidth,
}: {
    size: Size;
    storeCellsAcross: number;
    contentAreaWidth: number;
}) => {
    const navigate = useNavigate();
    const [filters, setFilters] = useState<string[]>([]);
    const [forced, rerender] = useState<number>(Date.now());
    const [searchParams] = useSearchParams();
    const [searchResult, setSearchResult] = useState<Store[] & { filters?: string[] }>([]);
    const q = searchParams.get(`q`);
    const c = searchParams.get(`c`);
    const sc = searchParams.get(`sc`);
    const { setStore } = useOutletContext<{ setStore: (v: Store | null) => void }>();
    let subCats: string[] = [];
    if (sc) {
        subCats = sc.split(`__`);
    }

    useEffect(() => {
        Axios.get(
            `${baseApi}${c ? `search` : `termsearch`}?${q ? `q=${encodeURIComponent(q)}` : ``}${
                c ? `c=${encodeURIComponent(c)}` : ``
            }${sc ? `&sc=${encodeURIComponent(sc)}` : ``}`
        ).then(r => {
            setSearchResult(r.data.results);
        });
    }, [q, c, sc]);

    useEffect(() => {
        if (!storeCellsAcross) {
            rerender(Date.now());
        }

        setFilters(searchResult.filters || []);
    }, [storeCellsAcross, forced, searchResult]);

    const getSc = (e: { target: { checked?: boolean } }, cat: string): string => {
        if (e.target.checked) {
            return `&sc=${encodeURIComponent([...subCats, cat].join(`__`))}`;
        }
        const result = subCats.filter(f => f !== cat);
        if (result.length) {
            return `&sc=${encodeURIComponent(result.join(`__`))}`;
        }
        return ``;
    };

    const goTo = (cat: string) => (e: React.MouseEvent<HTMLDivElement>) =>
        navigate(`/search?c=${encodeURIComponent(c!)}${getSc(e as { target: { checked?: boolean } }, cat)}`);

    const hasFilters = filters.length > 0 && c && size === `large`;

    return (
        <AppWrap style={{ paddingTop: `${TOPBARHEIGHT + 24}px` }} id="appwrap">
            {(c || q) && !(c && q) ? (
                <>
                    <div style={{ marginBottom: `40px` }} />
                    <ContentArea
                        id="contentarea"
                        style={hasFilters ? { width: `calc(100% - 325px)` } : {}}
                        className="left"
                    >
                        <H1>
                            {q && <span>Search results for &quot;{q}&quot;</span>}
                            {c && <span>{decodeURIComponent(c)}</span>}
                        </H1>
                    </ContentArea>

                    {!!searchResult.length && (
                        <GridArea {...{ contentAreaWidth, storeCellsAcross }} className="between wrap noflex">
                            {getDisplayableCells(searchResult, storeCellsAcross, size).map((a, i) => (
                                <LogoBox
                                    length={searchResult.length}
                                    key={i}
                                    index={i}
                                    {...{ setStore, storeCellsAcross, size, ...a }}
                                />
                            ))}
                        </GridArea>
                    )}
                    {!searchResult.length && <Box>No results</Box>}

                    {!!hasFilters && (
                        <ContentArea className="wrap noflex right">
                            <RightBox style={{ overflow: `hidden` }}>
                                <img style={{ width: `100%`, height: `100%` }} alt="" src={ibs} />
                            </RightBox>
                            <H2>Filter</H2>
                            <FilterBox>
                                {filters.map(f => (
                                    <div style={{ cursor: `pointer` }} onClick={goTo(f)} key={f}>
                                        <Checkbox
                                            style={{ verticalAlign: `middle`, marginRight: `5px`, padding: `3px` }}
                                            checked={subCats.includes(f)}
                                        />
                                        <span>{f}</span>
                                    </div>
                                ))}
                            </FilterBox>
                        </ContentArea>
                    )}
                </>
            ) : null}
        </AppWrap>
    );
};

export default Search;
